import PropTypes from 'prop-types';
import { useQueryData } from 'hook/useQueryData';
import { useFindBySpecificationQuery } from 'services/entityApi';
import { useIntl } from 'react-intl';
import useMetadata from 'hook/useMetadata';
import { useGetAllEntityTypesQuery } from 'services/metadataApi';
import { Stack } from '@mui/material';
import * as React from 'react';
import { useEffect } from 'react';
import { downloadPublicationCertificate, getDeepNestedFieldValue } from 'utils/entityUtils';
import { formatDate } from 'utils/formatTime';
import MDInput from 'components/MDInput';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ListIcon from '@mui/icons-material/List';
import MDButton from 'components/MDButton';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import MarkersList from 'pages/entities/marker/MarkersList';
import MDBox from '../../../components/MDBox';
import MDTypography from '../../../components/MDTypography';

/**
 * The FileMetadatas component fetches and displays metadata for a file. It also allows downloading of the file content.
 *
 * @component
 * @example
 * // Suppose entityId is 123 and fileContent a blob representing the file content
 * <FileMetadatas entityId={123} fileContent={fileContentBlob} />
 *
 * @param {number} entityId - The ID of the entity (file) to display metadata for.
 * @param {object} fileViewerConf - The fileViewer configuration
 * @param {func} onEntityUpdated - The fileViewer configuration
 * @returns {React.Component} The FileMetadatas component that displays the file metadata and a button to download the file content.
 */
function FileMetadatas({ entityId, fileViewerConf, onEntityUpdated }) {
  const { formatMessage: msg } = useIntl();
  const { getTitle: getEntityTypeTitle } = useMetadata(useGetAllEntityTypesQuery, 'entityType');
  const { data } = useQueryData(
    useFindBySpecificationQuery,
    {
      viewName: 'fileViewer',
      filter: `id = ${entityId}`,
      size: 1,
      jsonView: 'WithParent'
    },
    undefined,
    null
  );

  const { getValue: conf } = useConf(useGetConfigurationQuery);

  const fields = fileViewerConf?.fields ?? [];
  const entity = data && data.content?.length > 0 ? data.decode().content[0] : null;

  const handlePublicationCertificateBtnClick = () => {
    downloadPublicationCertificate(entity.id);
  };

  useEffect(() => {
    onEntityUpdated(entity);
  }, [onEntityUpdated, entity]);

  return entity ? (
    <Stack spacing={2}>
      <MDBox
        variant="gradient"
        bgColor="primary"
        borderRadius="lg"
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0
        }}
      >
        <ListIcon color="white" fontSize="large" />
        <MDTypography variant="h4" color="white" sx={{ mx: 1 }}>
          {msg({ id: 'fileviewer.metadata.title' })}
        </MDTypography>
      </MDBox>

      <MarkersList entityId={entityId} markerIconsConf={conf('ui.image.markers')} />

      <MDInput
        key="entityName"
        fullWidth
        multiline
        label={msg({ id: 'common.name' })}
        value={entity.name}
        InputProps={{
          readOnly: true
        }}
        variant="filled"
      />
      <MDInput
        key="entityTypeCode"
        fullWidth
        multiline
        label={msg({ id: 'common.type' })}
        value={getEntityTypeTitle(entity.entityTypeCode)}
        InputProps={{
          readOnly: true
        }}
        variant="filled"
      />
      {fields && fields.map((field, index) => buildField(field, entity, index))}
      {shouldDisplayPublicationCertificateBtn(entity) ? (
        <MDButton
          startIcon={<DownloadRoundedIcon />}
          color="primary"
          onClick={handlePublicationCertificateBtnClick}
          sx={{
            width: '300px',
            marginLeft: 'auto !important'
          }}
        >
          {msg({ id: 'action.download.publication.certificate' })}
        </MDButton>
      ) : null}
    </Stack>
  ) : null;

  function buildField(field, entity, index) {
    return (
      <MDInput
        key={index}
        fullWidth
        multiline
        label={msg({ id: `common.${field.accessor}` })}
        value={getFieldValue(field, entity)}
        InputProps={{
          readOnly: true
        }}
        variant="filled"
      />
    );
  }

  function shouldDisplayPublicationCertificateBtn(entity) {
    const hasValidHash = entity.hash && entity.hash.trim() !== '';
    const hasValidPublicationDate = entity.publicationDate && entity.publicationDate.trim() !== '';
    return hasValidHash && hasValidPublicationDate;
  }

  function getFieldValue(field, entity) {
    if (field.format) {
      return formatDate(getDeepNestedFieldValue(field.accessor, entity), field.format);
    }
    switch (field.accessor) {
      case 'hash':
        return formatHash(getDeepNestedFieldValue(field.accessor, entity));
      case 'contentLen':
        return convertBytesToMB(getDeepNestedFieldValue(field.accessor, entity));
      default:
        return getDeepNestedFieldValue(field.accessor, entity);
    }
  }

  function convertBytesToMB(bytes) {
    if (!bytes || bytes <= 0) return 0;
    const result = bytes / 1024 / 1024;
    return Number(result.toFixed(3));
  }

  function formatHash(hash) {
    return hash
      ?.toUpperCase()
      .match(/.{1,2}/g)
      .join(' ');
  }
}

FileMetadatas.defaultProps = {
  onEntityUpdated: () => {}
};

FileMetadatas.propTypes = {
  entityId: PropTypes.number.isRequired,
  fileViewerConf: PropTypes.object,
  onEntityUpdated: PropTypes.func // Expected: (entity: object) => void
};
export default FileMetadatas;
